import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, ArrowRight, Lock } from "lucide-react";
import axios from "axios";
import AuthContext from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GradeSelect = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const isStudent = user?.role === 'student';
  const [enrolledGrades, setEnrolledGrades] = useState([]);
  const [licensedGrades, setLicensedGrades] = useState(null);

  // Redirect unauthenticated visitors to /try-demo (wait for auth to finish loading first)
  useEffect(() => {
    if (!authLoading && !user) navigate('/try-demo', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gradesRes] = await Promise.all([
          axios.get(`${API}/grades`),
        ]);
        setGrades(gradesRes.data.grades);

        // If student, fetch their enrolled grade levels (already license-filtered by backend)
        if (user && user.role === 'student') {
          try {
            const profileRes = await axios.get(`${API}/student/profile`);
            setEnrolledGrades(profileRes.data.enrolled_grades || []);
            setLicensedGrades(profileRes.data.licensed_grades || null);
          } catch { /* no enrollment data */ }
        } else if (user && user.school_id) {
          // Teacher/admin: fetch school license
          try {
            const licRes = await axios.get(`${API}/schools/${user.school_id}/license`);
            setLicensedGrades(licRes.data.licensed_grades || null);
          } catch { /* no license data = unrestricted */ }
        }
      } catch (error) {
        console.error("Error fetching grades:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const gradeIcons = {
    "K": "K", "1": "1", "2": "2", "3-4": "3-4", "5-6": "5-6", "7-8": "7-8", "9-12": "HS"
  };

  const gradeColors = [
    "bg-purple-100 text-purple-700 border-purple-300",
    "bg-blue-100 text-blue-700 border-blue-300",
    "bg-cyan-100 text-cyan-700 border-cyan-300",
    "bg-green-100 text-green-700 border-green-300",
    "bg-yellow-100 text-yellow-700 border-yellow-300",
    "bg-orange-100 text-orange-700 border-orange-300",
    "bg-red-100 text-red-700 border-red-300"
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // For students: filter to only enrolled grades (already license-filtered by backend)
  // For teachers: filter by school license
  const isGradeAccessible = (gradeValue) => {
    if (isStudent && enrolledGrades.length > 0) return enrolledGrades.includes(gradeValue);
    if (licensedGrades && !isStudent) return licensedGrades.includes(gradeValue);
    return true;
  };

  const accessibleGrades = grades.filter(g => isGradeAccessible(g.value));
  const lockedGrades = grades.filter(g => !isGradeAccessible(g.value));

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="grade-select-page">
      {/* Student grade restriction banner */}
      {isStudent && enrolledGrades.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-300 px-6 py-4 mb-8" data-testid="student-grade-banner">
          <div className="flex items-center gap-3">
            <GraduationCap size={20} className="text-blue-700 flex-shrink-0" />
            <div>
              <p className="font-semibold text-blue-800">
                Your enrolled grade{enrolledGrades.length > 1 ? 's' : ''}: {enrolledGrades.join(', ')}
              </p>
              <p className="text-sm text-blue-600">You can only take assessments for your assigned grade level.</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
          <Link to="/" className="hover:text-[#002FA7]">Home</Link>
          <span>/</span>
          <span className="text-gray-900">Select Grade</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isStudent ? 'Your Assessment' : 'Select Grade Level'}
        </h1>
        <p className="text-gray-600 max-w-2xl">
          {isStudent
            ? 'Select your grade level to begin your MOSAIC assessment.'
            : 'Choose the appropriate grade level for the MOSAIC Language Proficiency Assessment. Each exam is tailored to the linguistic and cognitive development appropriate for that age group.'}
        </p>
      </div>

      {/* Grade Grid — Accessible grades */}
      <div className="grade-grid">
        {accessibleGrades.map((grade, index) => {
          const colorIdx = grades.indexOf(grade);
          return (
            <Link 
              key={grade.value}
              to={`/exam/setup/${grade.value}`}
              className={`grade-card opacity-0 animate-fade-in-up`}
              style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
              data-testid={`grade-card-${grade.value}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-16 h-16 ${gradeColors[colorIdx] || gradeColors[0]} border-2 flex items-center justify-center font-bold text-xl`}>
                  {gradeIcons[grade.value]}
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </div>
              <h2 className="grade-card-title">{grade.label}</h2>
              <p className="grade-card-description">{grade.description}</p>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <GraduationCap size={16} />
                  <span>63 Total Questions</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Locked grades for students/demo */}
      {lockedGrades.length > 0 && (
        <div className="mt-6">
          <div className="grade-grid">
            {lockedGrades.map((grade, index) => {
              const colorIdx = grades.indexOf(grade);
              return (
                <div
                  key={grade.value}
                  className="grade-card opacity-0 animate-fade-in-up relative cursor-not-allowed"
                  style={{ animationDelay: `${(accessibleGrades.length + index) * 0.1}s`, animationFillMode: 'forwards' }}
                  data-testid={`grade-card-${grade.value}`}
                >
                  <div className="absolute inset-0 bg-gray-100/70 z-10 flex items-center justify-center">
                    <div className="bg-white border-2 border-gray-300 px-4 py-2 flex items-center gap-2 shadow-sm">
                      <Lock size={16} className="text-gray-500" />
                      <span className="text-sm font-semibold text-gray-600">
                        Not enrolled
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-16 h-16 ${gradeColors[colorIdx] || gradeColors[0]} border-2 flex items-center justify-center font-bold text-xl opacity-40`}>
                      {gradeIcons[grade.value]}
                    </div>
                    <Lock className="text-gray-300" size={20} />
                  </div>
                  <h2 className="grade-card-title opacity-40">{grade.label}</h2>
                  <p className="grade-card-description opacity-40">{grade.description}</p>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <GraduationCap size={16} />
                      <span>63 Total Questions</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info Section — only for teachers/admins */}
      {!isStudent && (
        <div className="mt-12 border-2 border-gray-200 p-8">
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            About Grade Bands
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Elementary (K-2)</h4>
              <p>Focus on foundational language skills with visual supports. Questions use simple vocabulary and familiar contexts.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Elementary (3-4)</h4>
              <p>Introduction to academic language. Students engage with grade-level content across subjects.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Middle School (5-8)</h4>
              <p>Complex academic language with discipline-specific vocabulary. Multi-step tasks and extended responses.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">High School (9-12)</h4>
              <p>Advanced academic discourse. Students analyze, synthesize, and evaluate content across disciplines.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GradeSelect;
